/**
 * Loads Monaco and exactly what was asked for — nothing else.
 *
 * Nothing here is imported statically. `editor.api` is the API surface with no
 * editor contributions, so features are separate explicit imports; importing
 * `monaco-editor` bare would instead resolve to `esm/vs/index.js` and register
 * all ~90 bundled languages plus the LSP client.
 *
 * Monaco's language registry, theme service and feature contributions are all
 * process-global singletons, so every registration here is guarded and keyed.
 * Contrary to what an earlier version of this comment (and the design spec)
 * claimed, re-registering a language id does NOT throw in Monaco 0.56 —
 * `ModesRegistry.registerLanguage` just pushes the entry and fires a change
 * event again. The guards are still worthwhile: without them, a remount (or
 * a second `CodeEditor` requesting the same language) re-runs `register()`,
 * which re-registers completion/hover/etc. providers on top of the existing
 * ones — Monaco does not deduplicate providers, so the visible symptom is
 * duplicated entries in the suggestion list, not a crash.
 */
import { DEFAULT_FEATURES, FEATURES, LANGUAGES, type BuiltinLanguageId, type FeatureId } from './registry';
import { MONACO_THEME_ID, githubMonacoTheme, type MonacoThemeMode } from './theme';
import { configureMonacoWorker } from './worker';
import type { LanguageInput, MonacoApi, MonacoLanguageModule } from './types';

type ExternalLanguage = { id: string; load: () => Promise<MonacoLanguageModule> };

/**
 * A memoized async factory, keyed by string. The promise is stored
 * synchronously — before the factory's own first `await` — so two concurrent
 * callers for the same key share one in-flight attempt instead of each
 * starting the work (and, for Monaco registrations, racing to register the
 * same language id, which throws). A rejection removes the entry so the next
 * call gets a fresh attempt instead of replaying a cached failure forever.
 */
export function createAsyncCache<T>() {
  const cache = new Map<string, Promise<T>>();

  return {
    get(key: string, factory: () => Promise<T>): Promise<T> {
      const existing = cache.get(key);
      if (existing) return existing;

      const promise = factory().catch((error: unknown) => {
        cache.delete(key);
        throw error;
      });
      cache.set(key, promise);
      return promise;
    },
    clear(): void {
      cache.clear();
    },
  };
}

const API_CACHE_KEY = 'api';

const loadedFeatures = new Set<FeatureId>();
const registeredLanguages = new Set<BuiltinLanguageId>();
const apiCache = createAsyncCache<MonacoApi>();
const externalLanguageCache = createAsyncCache<void>();
let themesDefined = false;

/** Test seam. Monaco's own globals are not reset — only this module's bookkeeping. */
export function resetLoaderStateForTests(): void {
  loadedFeatures.clear();
  registeredLanguages.clear();
  apiCache.clear();
  externalLanguageCache.clear();
  themesDefined = false;
}

export interface PartitionedLanguages {
  builtins: BuiltinLanguageId[];
  externals: ExternalLanguage[];
  unknown: string[];
}

/**
 * Sort requested languages into built-ins, external loaders, and ids that match
 * neither. Unknown ids are reported rather than thrown: a missing grammar should
 * degrade to plain text, not blank the page.
 *
 * A built-in string and an external `{ id, load }` sharing the same id are
 * mutually exclusive: both are tracked in the same `seen` set keyed by id, so
 * whichever appears first in `inputs` wins and the other is dropped silently.
 */
export function partitionLanguages(inputs: LanguageInput[]): PartitionedLanguages {
  const builtins: BuiltinLanguageId[] = [];
  const externals: ExternalLanguage[] = [];
  const unknown: string[] = [];
  const seen = new Set<string>();

  for (const input of inputs) {
    if (typeof input === 'string') {
      if (seen.has(input)) continue;
      seen.add(input);
      if (input in LANGUAGES) {
        builtins.push(input as BuiltinLanguageId);
      } else {
        unknown.push(input);
      }
      continue;
    }
    if (seen.has(input.id)) continue;
    seen.add(input.id);
    externals.push(input);
  }

  return { builtins, externals, unknown };
}

export interface LoadMonacoOptions {
  languages: LanguageInput[];
  features?: FeatureId[];
  mode: MonacoThemeMode;
}

export async function loadMonaco({
  languages,
  features = DEFAULT_FEATURES,
  mode,
}: LoadMonacoOptions): Promise<MonacoApi> {
  configureMonacoWorker();

  // The API module is shared across every editor on the page; load it once.
  // A rejection is not cached — a later mount gets a fresh attempt instead of
  // being poisoned by one transient chunk-load failure.
  const monaco = await apiCache.get(
    API_CACHE_KEY,
    () => import('monaco-editor/editor/editor.api') as Promise<MonacoApi>,
  );

  const pendingFeatures = features.filter((id) => !loadedFeatures.has(id) && id in FEATURES);
  await Promise.all(pendingFeatures.map((id) => FEATURES[id]()));
  pendingFeatures.forEach((id) => loadedFeatures.add(id));

  const { builtins, externals, unknown } = partitionLanguages(languages);

  if (unknown.length > 0 && import.meta.env?.DEV) {
    console.warn(
      `[octopus-ui] Unknown language id(s): ${unknown.join(', ')}. ` +
        `Pass a built-in key (${Object.keys(LANGUAGES).join(', ')}) or ` +
        `{ id, load } for an external language. Falling back to plain text.`,
    );
  }

  await Promise.all(
    builtins
      .filter((id) => !registeredLanguages.has(id))
      .map(async (id) => {
        await LANGUAGES[id]();
        registeredLanguages.add(id);
      }),
  );

  // Externals share the same cache primitive as the API import: a late
  // joiner for an id that's already registering awaits the same in-flight
  // promise rather than assuming it's done, so it can never create a model
  // for a language before `register()` has actually run.
  await Promise.all(
    externals.map((language) =>
      externalLanguageCache.get(language.id, async () => {
        const module = await language.load();
        await module.register(monaco);
      }),
    ),
  );

  // GitHub Dark/Light are not built into Monaco, so they must be registered
  // before `setTheme` can resolve them. Registration is page-global and
  // idempotent, hence the guard; selection is not, hence the unguarded
  // `setTheme` below — a second editor mounting in a different mode has to be
  // able to change it.
  if (!themesDefined) {
    monaco.editor.defineTheme(MONACO_THEME_ID.dark, githubMonacoTheme('dark'));
    monaco.editor.defineTheme(MONACO_THEME_ID.light, githubMonacoTheme('light'));
    themesDefined = true;
  }
  monaco.editor.setTheme(MONACO_THEME_ID[mode]);

  return monaco;
}
